import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { PALISIS_URL } from "@/lib/site";
import VideoModal from "@/components/VideoModal";
import WeatherFx from "@/components/WeatherFx";

const SLIDES = [
  {
    src: "/girl-riding.jpg",
    alt: "A girl in her white and blue top smiling as she speeds down the toboggan track at Cool Runnings",
    label: "Real riders, real fun · rides from R110",
    pos: "28% center",
  },
  {
    src: "/dad-riding.jpg",
    alt: "Dad and his daughter speeding down the toboggan track at Cool Runnings",
    label: "Rides from R110 · pay per person, per ride",
    pos: "35% center",
  },
  {
    src: "/shoot-duo-riders.jpg",
    alt: "Two friends riding a toboggan together and smiling at Cool Runnings",
    label: "Ride together · birthday parties from R380pp",
    pos: "center 35%",
  },
  {
    src: "/park-track.jpg",
    alt: "The toboggan track winding down the hill towards Cape Town at Cool Runnings",
    label: "The 1.25 km track · rides from R110",
  },
  {
    src: "/park-launch.jpg",
    alt: "The polished toboggan track with the keep 25m distance sign at Cool Runnings",
    label: "Open 7 days a week · no booking needed to ride",
  },
];

const LINES = ["AFRICA'S FIRST", "DOWNHILL", "TOBOGGAN PARK"];

export default function Hero() {
  const [slide, setSlide] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    if (videoOpen) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [videoOpen]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={SLIDES[slide].src}
            alt={SLIDES[slide].alt}
            style={{ objectPosition: SLIDES[slide].pos || "center" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="ken absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/55 to-[#0a0a0b]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/85 via-[#0a0a0b]/30 to-transparent" />
      </motion.div>

      <WeatherFx />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <h1 className="max-w-5xl text-[clamp(2.6rem,8vw,6rem)] leading-[0.95] text-white" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-[#f8b818]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`hero-headline-line-${i}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a href={PALISIS_URL} target="_blank" rel="noopener noreferrer" className="btn-gold relative overflow-hidden shine" data-testid="hero-buy-tickets-btn">
            Buy Tickets
          </a>
          <button onClick={() => setVideoOpen(true)} className="btn-outline !border-white/80 !text-white" data-testid="hero-watch-film-btn">
            <Play size={16} /> Watch the video clip
          </button>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 gap-2" data-testid="hero-slide-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? "w-8 bg-[#f8b818]" : "w-3 bg-white/30"}`}
            data-testid={`hero-slide-dot-${i}`}
          />
        ))}
      </div>

      <AnimatePresence>
        {videoOpen && <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}
